import React, { PureComponent } from 'react';
import * as dates from '@simplybenefits/sb-dates';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import MetaClaimInputModal from './MetaClaimInputModal.js';
import SubmitReceiptModal from '../Common/SubmitReceiptModal.js';
import AddSupportingDocsModal from '../Common/AddSupportingDocsModal.js';
import ReviewClaimModal from './ReviewClaimModal.js';
import ClaimSuccessModal from '../Common/ClaimSuccessModal.js';
import ClaimProcessingModal from '../Common/ClaimProcessingModal.js';
import { getClaimCertMeta } from '../../../../actions/users.js';
import { submitClaim } from '../../../../actions/adjudication.js';
import { createTimer } from '../../../../common/createTimeTracker.js';

class Standard extends PureComponent {
    claimTimer = createTimer();
        
    state = {
        currentPage: 0,
        numPages: 5,
        meta: null,
        receiptPhoto: null,
        supportingPhotos: null,
        showSuccess: false,
        claimID: null,
        uid: null,
        userData: null,
        showClaimProcessingScreen: false
    };

    componentDidMount() {
        this.claimTimer.start();
        
        if (this.props.cert) {
            this._getCertInfo(this.props.cert);
        }
    }

    _previous = () => {
        let index = this.state.currentPage - 1;

        if (index < 0) {
            index = 0;
            return;
        }

        this.setState({
            currentPage: index
        });
    };

    _next = () => {
        let index = this.state.currentPage + 1;

        if (index > this.state.numPages - 1) {
            index = this.state.numPages - 1;
        }

        this.setState({
            currentPage: index
        });
    };

    /**
     * When each child view is completed we call this method to save the data in state
     * @param data
     * @param type
     * @private
     */
    _saveDataInState = (data, type) => {
        this.setState({
            [type]: data
        });
    };

    _submitClaim = async () => {
        this.setState({
            showClaimProcessingScreen: true
        });

        const { patient } = this.state.meta;

        const isDependent = [ 'Spouse', 'Common Law', 'Child' ].some((rel) => patient.name.includes(`(${ rel })`));

        const patientName = isDependent ? patient.name.split(' (')[0] : patient.name;
        const patientType = isDependent ? 'dependent' : 'primary';
        const dependentID = isDependent ? patient.id : null;

        // create a Form Data form for posting the images and meta data to the server
        const form = new FormData();
        form.append('autoTopOff', Boolean(this.state.meta.autoTopOff));
        form.append('patient', patientName);
        form.append('patientType', patientType);
        form.append('dependentID', dependentID);
        form.append('category', this.state.meta.category);
        form.append('service', this.state.meta.service);
        form.append('amountSubmitted', this.state.meta.amountSubmitted);
        form.append('serviceDate', dates.getUTC(this.state.meta.serviceDate));

        // add the documents (receipt and supporting docs)
        form.append("docs", null); // first entry must be null to fix CORS issue with Chrome (I know it's nuts)
        form.append("docs", this.state.receiptPhoto.file); // second entry is the receipt

        // attach each supporting document to the supporting docs array
        this.state.supportingPhotos && this.state.supportingPhotos.map((item) => {
            form.append("docs", item.file);
            return true;
        });

        if (this.props.type === 'Lifestyle Spending') {
            form.append('claimType', 'lsa');
        } else if (this.props.type === 'Health Spending') {
            form.append('claimType', 'hsa');
        } else {
            form.append('claimType', 'standard');
        }

        const claimCreationTimeSeconds = this.claimTimer.stop();

        form.append("claimCreationTimeSeconds", claimCreationTimeSeconds);

        const claim = await this.props.dispatch(submitClaim(this.state.userData.meta.userID, form));

        if (claim) {
            let amountClaimed = claim.amountPaid;
            const amountSubmitted = claim.amountSubmitted.toFixed(2);

            if (claim.autoTopOff) {
                amountClaimed += Number(claim.hsaUsedFromTopOff);
            }

            this.setState({
                showSuccess: true,
                amountClaimed: amountClaimed.toFixed(2),
                amountSubmitted,
                claimID: claim.claimID,
                uid: claim.uid,
                showClaimProcessingScreen: false
            });
        } else {
            this.setState({
                showClaimProcessingScreen: false,
                currentPage: 3
            });
        }
    };

    _getCertInfo = async (cert) => {
        if (!cert || cert.length <= 0) {
            return;
        }

        const userData = await this.props.dispatch(getClaimCertMeta(cert));

        if (userData) {
            this.setState({
                userData
            });
        }
    };

    render() {
        if (this.state.showSuccess) {
            return (
                <ClaimSuccessModal
                    amountClaimed={ Number(this.state.amountClaimed) || 0 }
                    patient={this.state.meta.patient.name}
                    onClose={this.props.close}
                    loader={this.props.loader}
                    uid={ this.state.uid }
                    adjudicationLink={`/adjudication/${ this.state.claimID }`}
                />
            );
        }

        if (this.state.showClaimProcessingScreen) {
            return (
                <ClaimProcessingModal />
            );
        }

        return (
            <>
                {
                    this.state.currentPage === 0 &&
                            <MetaClaimInputModal
                                meta={ this.state.meta }
                                saveData={ this._saveDataInState }
                                next={ this._next }
                                type={ this.props.type }
                                userData={ this.state.userData }
                                getCertInfo={ this._getCertInfo }
                                title={ `Create ${ this.props.type } Claim` }
                                onClose={ this.props.close }
                                loader={ this.props.loader }
                            />
                }

                {
                    this.state.currentPage === 1 &&
                            <SubmitReceiptModal
                                receiptPhoto={ this.state.receiptPhoto }
                                saveData={ this._saveDataInState }
                                next={ this._next }
                                type={ this.props.type }
                                userData={ this.state.userData }
                                title={ `Create ${ this.props.type } Claim` }
                                onClose={ this.props.close }
                                loader={ this.props.loader }
                                onBack={ this._previous } 
                            />
                }

                {
                    this.state.currentPage === 2 &&
                            <AddSupportingDocsModal
                                supportingPhotos={this.state.supportingPhotos}
                                saveData={ this._saveDataInState }
                                next={ this._next }
                                type={ this.props.type }
                                userData={ this.state.userData }
                                title={ `Create ${ this.props.type } Claim` }
                                onClose={ this.props.close }
                                loader={ this.props.loader }
                                onBack={ this._previous } 
                            />
                }

                {
                    this.state.currentPage === 3 &&
                            <ReviewClaimModal
                                meta={ this.state.meta }
                                receiptPhoto={ this.state.receiptPhoto }
                                supportingPhotos={ this.state.supportingPhotos }
                                submitClaim={ this._submitClaim }
                                type={ this.props.type }
                                loader={ this.props.loader }
                                userData={ this.state.userData }
                                title={ `Create ${ this.props.type } Claim` }
                                onClose={ this.props.close }
                                onBack={ this._previous } 
                            />
                }
            </>
        );
    }
}

Standard.propTypes = {
    master: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    master: state.master,
    loader: state.loader,
    submitClaimLoading: state.adjudication.loading
});

export default connect(mapStateToProps)(Standard);